import type { LocaleId } from "./locale.ts";
import { deLocale } from "./locales/de.ts";
import { enLocale } from "./locales/en.ts";
import { esLocale } from "./locales/es.ts";
import { frLocale } from "./locales/fr.ts";
import { itLocale } from "./locales/it.ts";
import { nlLocale } from "./locales/nl.ts";
import { ptLocale } from "./locales/pt.ts";
import { zhLocale } from "./locales/zh.ts";
import type { GameStrings, LocaleStrings, SiteStrings } from "./types.ts";

const localeRegistry: Record<LocaleId, LocaleStrings> = {
  en: enLocale,
  it: itLocale,
  fr: frLocale,
  es: esLocale,
  pt: ptLocale,
  de: deLocale,
  zh: zhLocale,
  nl: nlLocale,
};

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function deepMergeWithFallback<T>(base: T, override: T): T {
  if (Array.isArray(base) && Array.isArray(override)) {
    if (override.length === 0) {
      return base;
    }
    return override.map((item, index) => {
      const baseItem = base[index];
      if (isPlainObject(baseItem) && isPlainObject(item)) {
        return deepMergeWithFallback(baseItem, item);
      }
      return item ?? baseItem;
    }) as T;
  }

  if (!isPlainObject(base) || !isPlainObject(override)) {
    return override ?? base;
  }

  const result = { ...base } as Record<string, unknown>;
  for (const key of Object.keys(base)) {
    const baseVal = base[key];
    const overrideVal = override[key];
    if (overrideVal === undefined) {
      result[key] = baseVal;
    } else if (Array.isArray(baseVal) && Array.isArray(overrideVal)) {
      result[key] = deepMergeWithFallback(baseVal, overrideVal);
    } else if (isPlainObject(baseVal) && isPlainObject(overrideVal)) {
      result[key] = deepMergeWithFallback(baseVal, overrideVal);
    } else {
      result[key] = overrideVal;
    }
  }
  return result as T;
}

function resolveLocaleStrings(locale: LocaleId): LocaleStrings {
  const localeStrings = localeRegistry[locale];
  if (locale === "en") {
    return localeStrings;
  }
  return deepMergeWithFallback(enLocale, localeStrings);
}

export function getGameStrings(locale: LocaleId): GameStrings {
  return resolveLocaleStrings(locale).game;
}

export function getSiteStrings(locale: LocaleId): SiteStrings {
  return resolveLocaleStrings(locale).site;
}

export function getLanguageLabel(uiLocale: LocaleId, language: LocaleId): string {
  return resolveLocaleStrings(uiLocale).game.languageLabels[language];
}
